import React from "react";
import Collapse from "@material-ui/core/Collapse";
import ArrowForwardRoundedIcon from "@material-ui/icons/ArrowForwardRounded";
import { getOrderDiscount } from "../../../Helpers/promotions";
import { formatMoney } from "../presentation/money";
import {
  resolveContextSummary,
  resolveMinimumOrder,
  resolveMovProgress,
} from "./contextSummary";
import StorefrontActionButton from "../ActionButton/StorefrontActionButton";
import { getQuoteDisplayRounding, isQuoteVatIncluded } from "../../../checkout-v2/domain/quoteDisplay";
import {
  buildCheckoutLines,
  getCheckoutLineId,
  getEffectiveCheckoutLines,
  createCartFingerprint,
} from "../../../checkout-v2/domain/cartContext";
import {
  convertMoneyForDisplay,
  moneyToMajor,
} from "../../../checkout-v2/domain/money";
import "./HomeCartBar.css";
import "../../../checkout-v2/styles/typography.css";

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  `${left}` === `${right}`;

export const resolveHomeDeliveryCharge = ({
  store = {},
  settings = {},
  branch = {},
  address = {},
  cart = {},
} = {}) => {
  // Guest/reset checkout state can explicitly contain null, not just undefined.
  branch = branch || {};
  address = address || {};
  const isPickup = branch.isPickup === true || Number(address.selectedDeliveryType) === 1;
  if (isPickup || Number(cart.charged_items) <= 0) return 0;
  const source = settings.is_branch_based_ordering
    ? store.branch_delivery_charges
    : store.areas || store.delivery_charges;
  const selectedId = settings.is_branch_based_ordering ? branch.bdc : address.area;
  const selected = (Array.isArray(source) ? source : []).find((entry) =>
    sameId(entry && entry.id, selectedId),
  );
  const amount = Number(selected && selected.price);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

export const resolveHomeCartItemCount = (cart = {}, validation = {}) => {
  const lines = getEffectiveCheckoutLines(buildCheckoutLines(cart), validation);
  return lines.reduce((count, line) => {
    const quantity = Number(line && line.quantity);
    return count + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
  }, 0);
};

const finiteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const resolveHomeCartLineSubtotal = (cart = {}) => {
  const lines = (Array.isArray(cart.cart) ? cart.cart : []).concat(
    Array.isArray(cart.promo_items) ? cart.promo_items : [],
  );
  const hasFinitePrice = (value) =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value));
  const pricedLines = lines.filter(
    (line) =>
      line &&
      (hasFinitePrice(line.total_price) || hasFinitePrice(line.price)),
  );
  if (!lines.length) return 0;
  // Older persisted carts do not always contain a per-line price. Keep the
  // legacy aggregate only for that compatibility case; once line prices are
  // present they are safer than a potentially stale aggregate.
  if (!pricedLines.length || pricedLines.length !== lines.length)
    return Math.max(finiteNumber(cart.price), 0);
  return Math.max(
    pricedLines.reduce(
      (total, line) =>
        total +
        finiteNumber(line?.total_price ?? line?.price) *
          Math.max(finiteNumber(line?.quantity, 1), 0) +
        finiteNumber(line?.quantityExcludedOptionsTotal),
      0,
    ),
    0,
  );
};

// These checkout prerequisites do not invalidate an already-calculated price.
// Keep unknown errors fail-closed; this must never authorize order placement.
const PRICE_INDEPENDENT_ERRORS = new Set([
  "payment_method_required",
  "payment_provider_contact_required",
  "payment_provider_unavailable",
]);
const hasOnlyPaymentPrerequisites = value => value?.errors?.length > 0 &&
  value.errors.every(error => PRICE_INDEPENDENT_ERRORS.has(typeof error === "string" ? error : error.code));

export const homeCartValidationMatchesCart = (cart = {}, validation = {}) => {
  if ((validation.valid !== true && !hasOnlyPaymentPrerequisites(validation)) || !validation.checkedAt) return false;
  const requestLines = buildCheckoutLines(cart);
  if (!requestLines.length) return false;
  const responseLines = [
    ...(validation.retainedLines || []),
    ...(validation.adjustedLines || []),
    ...(validation.removedLines || []),
  ];
  const responseById = new Map(
    responseLines.map((line, index) => [
      `${line.lineId || line.line_id || getCheckoutLineId(line, index)}`,
      line,
    ]),
  );
  const activeIds = new Set(getEffectiveCheckoutLines(requestLines, validation).map(line => `${line.lineId}`));
  if ([...(validation.retainedLines || []), ...(validation.adjustedLines || [])]
      .some(line => !activeIds.has(`${line.lineId || line.line_id}`))) return false;
  return requestLines.every((line) => {
    const response = responseById.get(`${line.lineId}`);
    if (!response) return false;
    const requestedQuantity =
      response.originalQuantity ??
      response.original_quantity ??
      response.requestedQuantity ??
      response.requested_quantity ??
      response.quantity;
    return finiteNumber(requestedQuantity, -1) === finiteNumber(line.quantity, -2);
  });
};

const displayMajor = (value, displayCurrency) =>
  moneyToMajor(convertMoneyForDisplay(value, displayCurrency));

export const resolveHomeCartTotals = ({
  cart = {},
  store = {},
  settings = {},
  branch = {},
  address = {},
  validation = {},
  quote = null,
  requireQuote = false,
} = {}) => {
  const validationIsCurrent = homeCartValidationMatchesCart(cart, validation);
  if (resolveHomeCartItemCount(cart, validation) <= 0)
    return { subtotal: 0, discount: 0, delivery: 0, total: 0 };
  const quoteMatchesValidation =
    validationIsCurrent &&
    (quote?.valid === true || hasOnlyPaymentPrerequisites(quote)) &&
    // A bootstrap quote can price the cart before a payment method is chosen;
    // requiresRequote blocks placement, not display of this exact cart's price.
    quote?.refreshing !== true &&
    quote?.pricingStatus !== "pending" &&
    quote?.clientCartFingerprint === createCartFingerprint(getEffectiveCheckoutLines(buildCheckoutLines(cart), validation)) &&
    quote?.orderTotal !== null &&
    quote?.orderTotal !== undefined &&
    (!quote.contextFingerprint ||
      !validation.contextFingerprint ||
      quote.contextFingerprint === validation.contextFingerprint);
  const displayCurrency =
    quoteMatchesValidation && quote?.displayCurrency
      ? quote.displayCurrency
      : validationIsCurrent
        ? validation.displayCurrency
        : null;

  if (quoteMatchesValidation) {
    const additionalFee = displayMajor(quote.additionalFee, displayCurrency) || 0;
    const giftWrapping = displayMajor(quote.giftWrapping, displayCurrency) || 0;
    const vat = displayMajor(quote.vatAmount, displayCurrency) || 0;
    const rounding = moneyToMajor(getQuoteDisplayRounding(quote, displayCurrency));
    return {
      ...(additionalFee ? { additionalFee } : {}),
      ...(giftWrapping ? { giftWrapping } : {}),
      ...(vat ? { vat, vatIncluded: isQuoteVatIncluded(quote) } : {}),
      ...(rounding ? { rounding } : {}),
      subtotal: Math.max(displayMajor(quote.subtotal, displayCurrency) || 0, 0),
      discount: Math.max(
        displayMajor(quote.discountTotal, displayCurrency) || 0,
        0,
      ),
      delivery: Math.max(
        displayMajor(quote.deliveryRate, displayCurrency) || 0,
        0,
      ),
      total: Math.max(
        displayMajor(quote.orderTotal, displayCurrency) || 0,
        0,
      ),
    };
  }

  const validatedSubtotal = validationIsCurrent
    ? displayMajor(validation.subtotal, displayCurrency)
    : null;
  const subtotal =
    validatedSubtotal === null
      ? resolveHomeCartLineSubtotal(cart)
      : Math.max(validatedSubtotal, 0);
  let discount = 0;
  try {
    discount = getOrderDiscount(
      subtotal,
      Array.isArray(cart.cart) ? cart.cart : [],
      cart.promos,
      cart.discounts,
      Array.isArray(store.smart_promos) ? store.smart_promos : [],
    );
  } catch (error) {
    discount = 0;
  }
  discount = Math.min(Math.max(Number(discount) || 0, 0), subtotal);
  const validatedDelivery = validationIsCurrent
    ? displayMajor(validation.deliveryRate, displayCurrency)
    : null;
  const delivery =
    validatedDelivery === null
      ? resolveHomeDeliveryCharge({
          store,
          settings,
          branch,
          address,
          cart,
        })
      : Math.max(validatedDelivery, 0);
  return {
    ...(requireQuote ? { pending: true } : {}),
    subtotal,
    discount,
    delivery,
    total: Math.max(subtotal - discount + delivery, 0),
  };
};

const Row = ({ label, value, strong = false }) => (
  <div className={`f0-cart-bar__row${strong ? " f0-cart-bar__row--strong" : ""}`}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

class HomeCartBar extends React.Component {
  state = { expanded: false };
  touchOrigin = null;
  suppressClickUntil = 0;

  setBarElement = (element) => {
    if (this.barElement === element) return;
    if (this.barElement) this.barElement.removeEventListener("touchmove", this.handleTouchMove);
    if (this.barObserver) this.barObserver.disconnect();
    this.barElement = element;
    // React 16 delegates touch events to document, where browsers can treat
    // them as passive. Only claim a vertical swipe that started in this bar.
    if (element) element.addEventListener("touchmove", this.handleTouchMove, { passive: false });
    if (element) {
      const measure = () => {
        const height = Math.ceil(element.getBoundingClientRect().height);
        if (height) document.documentElement.style.setProperty("--home-cart-bar-height", `${height + 12}px`);
      };
      measure();
      if (typeof ResizeObserver !== "undefined") {
        this.barObserver = new ResizeObserver(measure);
        this.barObserver.observe(element);
      }
    } else document.documentElement.style.removeProperty("--home-cart-bar-height");
  };

  setExpanded = (expanded) => this.setState({ expanded });

  handleTouchStart = (event) => {
    this.touchOrigin = null;
    this.suppressClickUntil = 0;
    if (event.touches?.length !== 1) return;
    const touch = event.touches[0];
    const bounds = event.currentTarget.getBoundingClientRect();
    // Exclude just the top 16px so menu scrolling can pass over that edge.
    // The rest works in both directions, including the expanded breakdown.
    if (touch.clientY < bounds.top + 16 || touch.clientY > bounds.bottom) return;
    this.touchOrigin = { x: touch.clientX, y: touch.clientY, id: touch.identifier };
  };

  handleTouchMove = (event) => {
    const origin = this.touchOrigin;
    if (!origin) return;
    if (event.touches.length !== 1) {
      this.touchOrigin = null;
      return;
    }
    const touch = event.touches[0];
    if (touch.identifier !== origin.id) return;
    const deltaY = touch.clientY - origin.y;
    const deltaX = touch.clientX - origin.x;
    if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.touchOrigin = null;
      return;
    }
    if (Math.abs(deltaY) > 10 && Math.abs(deltaY) > Math.abs(deltaX) * 1.25 &&
      ((deltaY < 0 && !this.state.expanded) || (deltaY > 0 && this.state.expanded))) {
      // Prevent page scrolling from taking over/cancelling the swipe.
      if (event.cancelable) event.preventDefault();
    }
  };

  handleTouchEnd = (event) => {
    const origin = this.touchOrigin;
    this.touchOrigin = null;
    if (!origin || event.touches?.length) return;
    const touch = Array.from(event.changedTouches || []).find((item) => item.identifier === origin.id);
    if (!touch) return;
    const deltaY = touch.clientY - origin.y;
    const deltaX = touch.clientX - origin.x;
    if (Math.abs(deltaY) < 32 || Math.abs(deltaY) <= Math.abs(deltaX) * 1.25) return;
    if ((deltaY < 0 && !this.state.expanded) || (deltaY > 0 && this.state.expanded)) {
      this.suppressClickUntil = Date.now() + 500;
      this.setExpanded(deltaY < 0);
    }
  };

  handleTouchCancel = () => { this.touchOrigin = null; };

  handleClickCapture = (event) => {
    // A swipe that started on the title/CTA must not also activate that button.
    // A new touch resets this guard; keyboard activation remains unaffected.
    if (event.detail !== 0 && Date.now() < this.suppressClickUntil) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  render() {
    const {
      settings = {},
      store = {},
      branch = {},
      address = {},
      cart = {},
      validation = {},
      quote = null,
      requireQuote = false,
      language = "english",
      actionLabel,
      onViewCart,
    } = this.props;
    const itemCount = resolveHomeCartItemCount(cart, validation);
    if (itemCount <= 0) return null;

    const arabic = language === "arabic";
    const context = resolveContextSummary({
      store,
      settings,
      branch,
      address,
      language,
    });
    const totals = resolveHomeCartTotals({
      cart,
      store,
      settings,
      branch,
      address,
      validation,
      quote,
      requireQuote,
    });
    const minimum =
      settings.is_branch_based_ordering && !context.resolved
        ? 0
        : resolveMinimumOrder({ settings, context });
    const mov = resolveMovProgress({ minimum, cartTotal: totals.subtotal });
    const movUnmet =
      settings.show_mov_meter !== false && minimum > 0 && mov.remaining > 0;
    const money = (value) => formatMoney(value, { settings, language });
    const { expanded } = this.state;
    const reviewLabel = actionLabel || (arabic ? "مراجعة الطلب" : "Review order");

    return (
      <aside
        ref={this.setBarElement}
        aria-label={arabic ? "ملخص السلة" : "Cart summary"}
        className={`f0-cart-bar storefront-v2-surface${expanded ? " f0-cart-bar--expanded" : ""}`}
        dir={arabic ? "rtl" : "ltr"}
        style={{ "--f0-accent": settings.dark || "#0099cc" }}
        onTouchEnd={this.handleTouchEnd}
        onTouchStart={this.handleTouchStart}
        onTouchCancel={this.handleTouchCancel}
        onClickCapture={this.handleClickCapture}
      >
        {this.props.promotionProgress}
        <button
          aria-expanded={expanded}
          aria-label={expanded ? (arabic ? "إخفاء التفاصيل" : "Collapse details") : (arabic ? "عرض التفاصيل" : "Expand details")}
          className="f0-cart-bar__summary"
          onClick={() => this.setExpanded(!expanded)}
          type="button"
        >
          <span className="f0-cart-bar__quantity">{itemCount}</span>
          <span className="f0-cart-bar__title">
            <strong>{reviewLabel}</strong>
            <small>
              {context.isPickup
                ? arabic
                  ? "استلام"
                  : "Pickup"
                : arabic
                  ? `يشمل التوصيل ${money(totals.delivery)}`
                  : `incl. delivery ${money(totals.delivery)}`}
            </small>
          </span>
          <span className="f0-cart-bar__amount">
            <strong>{totals.pending ? (arabic ? "تأكيد الإجمالي في السلة" : "Confirm total in cart") : money(totals.total)}</strong>
            <small>{expanded ? (arabic ? "إخفاء" : "Hide") : (arabic ? "التفاصيل" : "Details")} <span className="f0-cart-bar__chevron" aria-hidden="true">⌃</span></small>
          </span>
        </button>

        {movUnmet && (
          <div className="f0-cart-bar__mov" aria-live="polite">
            <div className="f0-cart-bar__mov-track">
              <span
                style={{
                  background: settings.dark || "#0099cc",
                  width: `${mov.progress * 100}%`,
                }}
              />
            </div>
            <p>
              {arabic
                ? `أضف ${money(mov.remaining)} للوصول إلى الحد الأدنى`
                : `Add ${money(mov.remaining)} to reach the minimum`}
            </p>
          </div>
        )}

        <Collapse in={expanded} timeout={240} unmountOnExit>
          <div className="f0-cart-bar__details">
            {totals.pending ? <p>{arabic ? "افتح السلة لتأكيد أحدث الأسعار والخصومات." : "Open your cart to confirm the latest prices and discounts."}</p> : <>
            <Row label={arabic ? "المجموع الفرعي" : "Subtotal"} value={money(totals.subtotal)} />
            {totals.discount > 0 && (
              <Row label={arabic ? "الخصم" : "Discount"} value={`− ${money(totals.discount)}`} />
            )}
            {!context.isPickup && (
              <Row label={arabic ? "التوصيل" : "Delivery"} value={money(totals.delivery)} />
            )}
            {totals.additionalFee > 0 && <Row label={arabic ? "رسوم إضافية" : "Additional fee"} value={money(totals.additionalFee)} />}
            {totals.giftWrapping > 0 && <Row label={arabic ? "تغليف الهدية" : "Gift wrapping"} value={money(totals.giftWrapping)} />}
            {totals.vat > 0 && <Row label={totals.vatIncluded ? (arabic ? "الضريبة (مشمولة)" : "VAT (included)") : (arabic ? "الضريبة" : "VAT")} value={money(totals.vat)} />}
            {!!totals.rounding && <Row label={arabic ? "فرق تقريب العملة" : "Currency rounding"} value={money(totals.rounding)} />}
            <Row label={arabic ? "الإجمالي" : "Total"} value={money(totals.total)} strong />
            </>}
            <StorefrontActionButton
              className="f0-cart-bar__cta"
              disabled={movUnmet || !expanded}
              fullWidth
              onClick={onViewCart}
            >
              {movUnmet
                ? arabic
                  ? "أضف منتجات للمتابعة"
                  : "Add items to continue"
                : reviewLabel}
            </StorefrontActionButton>
          </div>
        </Collapse>

        <Collapse in={!expanded} timeout={240} unmountOnExit>
          <StorefrontActionButton
            className="f0-cart-bar__view"
            color="primary"
            endIcon={<ArrowForwardRoundedIcon aria-hidden="true" />}
            onClick={onViewCart}
            disabled={expanded}
            tone="quiet"
          >
            {arabic ? "عرض السلة" : "View cart"}
          </StorefrontActionButton>
        </Collapse>
      </aside>
    );
  }
}

export default HomeCartBar;
